/**
 * Who a guest is, from the device's point of view — the pure half.
 *
 * A guest account belongs to a DEVICE, and a device proves itself with two
 * values it keeps: an id (where to look) and a secret (proof it is the same
 * device). This file decides what those are and what makes them acceptable;
 * where they are kept is platform-specific and lives beside it.
 *
 * The secret matters more than the id. Android's ANDROID_ID and iOS's
 * identifierForVendor can be read by other software on the same phone, so an
 * id-only scheme would hand a child's coverage, errors and certificates to
 * anything that can read one. The id is a lookup key; the secret is the lock.
 */

/** The server refuses anything shorter — a short secret is a guessable one. */
export const MIN_SECRET_LENGTH = 32;
export const MAX_DEVICE_ID_LENGTH = 200;

export type GuestKeys = { deviceId: string; deviceSecret: string };

/** Whether a stored pair is still usable, or must be made afresh. */
export function usableKeys(keys: Partial<GuestKeys> | null | undefined): keys is GuestKeys {
  if (!keys) return false;
  const { deviceId, deviceSecret } = keys;
  if (typeof deviceId !== 'string' || !deviceId || deviceId.length > MAX_DEVICE_ID_LENGTH) {
    return false;
  }
  return typeof deviceSecret === 'string' && deviceSecret.length >= MIN_SECRET_LENGTH;
}

/**
 * A hex secret from real random bytes.
 *
 * Takes the generator rather than reaching for one: expo-crypto on the phone,
 * window.crypto in a browser, and a test can hand in a known sequence and
 * assert what came out instead of asserting that something random happened.
 */
export function secretFrom(bytes: Uint8Array): string {
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * The device id to use, given what the platform could tell us.
 *
 * A platform id is preferred because it survives the app's own storage being
 * cleared. When there is none — a browser, or a platform that refuses — a
 * random id is generated and kept, which is a weaker promise (clearing site
 * data loses the account) and an honest one: the alternative is fingerprinting
 * the reader, which this product will not do.
 */
export function deviceIdFrom(platformId: string | null, fallback: string): string {
  const id = typeof platformId === 'string' ? platformId.trim() : '';
  if (id && id.length <= MAX_DEVICE_ID_LENGTH) return id;
  return fallback;
}
